import { useEffect, useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Stack, TextField,
} from '@mui/material';
import {
  createProduct,
  getAllCategories,
  getAllTributaryGroups,
  getSubCategoriesByCategory,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import {
  formatBRLFromNumber,
  formatCurrencyInputBR,
  parseBRLToNumber,
} from '../utils/decimalBr';

const eanValido = (ean) => {
  const d = String(ean || '').replace(/\D/g, '');
  if (!d || d.length < 8) return '';
  return d;
};

/**
 * Cadastro rápido de produto a partir de um item do XML da NF-e.
 * Devolve o produto criado via onCreated({ id, label }).
 */
export default function NfeXmlNovoProdutoDialog({
  open,
  item,
  estabelecimentoId = 1,
  onClose,
  onCreated,
}) {
  const [descricao, setDescricao] = useState('');
  const [codigo, setCodigo] = useState('');
  const [ean, setEan] = useState('');
  const [ncm, setNcm] = useState('');
  const [unidade, setUnidade] = useState('UN');
  const [valorCusto, setValorCusto] = useState('');
  const [valorVarejo, setValorVarejo] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [subCategoriaId, setSubCategoriaId] = useState('');
  const [grupoTributarioId, setGrupoTributarioId] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [subs, setSubs] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !item) return;
    setError('');
    setDescricao(item.descricao || '');
    setCodigo(item.codigoXml || '');
    setEan(eanValido(item.ean));
    setNcm(String(item.ncm || '').replace(/\D/g, '').slice(0, 8));
    setUnidade((item.unidade || 'UN').slice(0, 6).toUpperCase() || 'UN');
    const custo = Number(item.custoNovo) || Number(item.valorUnitario) || 0;
    setValorCusto(custo > 0 ? formatBRLFromNumber(custo) : '');
    setValorVarejo(custo > 0 ? formatBRLFromNumber(custo) : '');
    setCategoriaId('');
    setSubCategoriaId('');
    setGrupoTributarioId('');
    setSubs([]);
  }, [open, item]);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setLoadingMeta(true);
      try {
        const [cats, groups] = await Promise.all([
          getAllCategories({ indice: 1, qtde: 500 }),
          getAllTributaryGroups({ indice: 1, qtde: 500 }),
        ]);
        if (cancel) return;
        const catItems = cats.data?.items || [];
        const grpItems = groups.data?.items || [];
        setCategorias(Array.isArray(catItems) ? catItems : []);
        setGrupos(Array.isArray(grpItems) ? grpItems : []);
        if (Array.isArray(grpItems) && grpItems.length === 1) {
          setGrupoTributarioId(String(grpItems[0].id));
        }
      } catch (e) {
        if (!cancel) setError(getApiErrorMessage(e, 'Falha ao carregar grupos.'));
      } finally {
        if (!cancel) setLoadingMeta(false);
      }
    })();
    return () => { cancel = true; };
  }, [open]);

  useEffect(() => {
    if (!categoriaId) {
      setSubs([]);
      setSubCategoriaId('');
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const { data } = await getSubCategoriesByCategory(Number(categoriaId));
        if (cancel) return;
        const list = Array.isArray(data) ? data : (data?.items || []);
        setSubs(list);
        if (list.length === 1) setSubCategoriaId(String(list[0].id));
      } catch {
        if (!cancel) setSubs([]);
      }
    })();
    return () => { cancel = true; };
  }, [categoriaId]);

  const salvar = async () => {
    setError('');
    if (!descricao.trim()) {
      setError('Informe a descrição.');
      return;
    }
    if (!categoriaId || !subCategoriaId) {
      setError('Selecione grupo e subgrupo.');
      return;
    }
    if (!grupoTributarioId) {
      setError('Selecione o grupo tributário.');
      return;
    }
    const ncmLimpo = String(ncm || '').replace(/\D/g, '');
    if (ncmLimpo.length !== 8) {
      setError('Informe o NCM com 8 dígitos (vem do XML).');
      return;
    }
    const varejo = parseBRLToNumber(valorVarejo);
    if (varejo === null || varejo < 0) {
      setError('Informe o preço de varejo.');
      return;
    }
    const custo = parseBRLToNumber(valorCusto);

    setSaving(true);
    try {
      const eanLimpo = eanValido(ean);
      const precos = [
        {
          tabelaPrecoId: 1,
          estabelecimentoId: Number(estabelecimentoId) || 1,
          tipo: 0,
          valor: varejo,
        },
      ];
      if (custo != null) {
        precos.push({
          tabelaPrecoId: 1,
          estabelecimentoId: Number(estabelecimentoId) || 1,
          tipo: 3,
          valor: custo,
        });
      }

      const payload = {
        estabelecimentoId: Number(estabelecimentoId) || 1,
        ativo: true,
        balanca: 0,
        codigo: (codigo || '').trim() || null,
        ean: eanLimpo || null,
        descricao: descricao.trim(),
        unidade: (unidade || 'UN').trim().toUpperCase() || 'UN',
        categoriaId: Number(categoriaId),
        subCategoriaId: Number(subCategoriaId),
        grupoTributarioId: Number(grupoTributarioId),
        ncm: ncmLimpo,
        cest: item?.cest ? String(item.cest).replace(/\D/g, '') || null : null,
        qtdAtacado: 0,
        codigosAdicionais: [],
        precos,
      };

      // Se o código do XML for numérico/curto demais, ainda assim envia;
      // conflito de código → API responde Conflict e mostramos a mensagem.
      const { data } = await createProduct(payload);
      const criado = data?.id ? data : data;
      const id = criado?.id;
      if (!id) throw new Error('Produto criado sem id.');

      onCreated?.({
        id,
        codigo: criado.codigo || codigo.trim(),
        descricao: criado.descricao || descricao.trim(),
        label: `${criado.codigo || codigo.trim() || id} · ${criado.descricao || descricao.trim()}`,
        custo: parseBRLToNumber(valorCusto) || 0,
        varejo: parseBRLToNumber(valorVarejo) || 0,
      });
      onClose?.();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível cadastrar o produto.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Cadastrar produto do XML</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {loadingMeta && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              Carregando grupos...
            </Stack>
          )}
          <TextField
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            fullWidth
            required
            autoFocus
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Código"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              fullWidth
              helperText="Vazio = código automático"
            />
            <TextField
              label="EAN"
              value={ean}
              onChange={(e) => setEan(e.target.value)}
              fullWidth
            />
            <TextField
              label="NCM"
              value={ncm}
              onChange={(e) => setNcm(e.target.value.replace(/\D/g, '').slice(0, 8))}
              required
              helperText="Importado do XML da NF-e"
              fullWidth
            />
            <TextField
              label="Unidade"
              value={unidade}
              onChange={(e) => setUnidade(e.target.value)}
              sx={{ minWidth: 100 }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Custo (XML)"
              value={valorCusto}
              onChange={(e) => setValorCusto(formatCurrencyInputBR(e.target.value))}
              fullWidth
            />
            <TextField
              label="Varejo"
              value={valorVarejo}
              onChange={(e) => setValorVarejo(formatCurrencyInputBR(e.target.value))}
              fullWidth
              required
            />
          </Stack>
          <TextField
            select
            label="Grupo"
            value={categoriaId}
            onChange={(e) => {
              setCategoriaId(e.target.value);
              setSubCategoriaId('');
            }}
            fullWidth
            required
          >
            {categorias.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.descricao || c.nome || `Grupo ${c.id}`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Subgrupo"
            value={subCategoriaId}
            onChange={(e) => setSubCategoriaId(e.target.value)}
            fullWidth
            required
            disabled={!categoriaId}
          >
            {subs.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.descricao || s.nome || `Sub ${s.id}`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Grupo tributário"
            value={grupoTributarioId}
            onChange={(e) => setGrupoTributarioId(e.target.value)}
            fullWidth
            required
          >
            {grupos.map((g) => (
              <MenuItem key={g.id} value={String(g.id)}>
                {g.descricao || g.nome || `GT ${g.id}`}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={salvar} disabled={saving || loadingMeta}>
          {saving ? 'Salvando...' : 'Cadastrar e vincular'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
